<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  DAYS_PER_WEEK,
  dayKey,
  daysBetween,
  eventClasses,
  eventSpan,
  formatTimeRange,
  isAllDayEvent,
  packLanes,
  useCalendarFormatters,
} from '@/composables/event-calendar'
import { pxToRem } from '@/lib/utils'
import { ABSENCE_ICON, eventTypeEnum } from '@/stores/schedule.store'
import { useWebSiteStore } from '@/stores/web-site.store'

const LANE_HEIGHT = 1.375
const DATE_HEADER_HEIGHT = 1.875
const LANE_PADDING = 0.25

const props = defineProps({
  month: {
    type: Object,
    required: true,
  },
  rangeStart: {
    type: Object,
    required: true,
  },
  rangeEnd: {
    type: Object,
    required: true,
  },
  events: {
    type: Array,
    required: true,
  },
  now: {
    type: Object,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['event-click', 'create', 'day-click'])

const { t, locale } = useI18n()
const webSiteStore = useWebSiteStore()
const formatters = computed(() => useCalendarFormatters(locale.value))

const rootRef = ref(null)
const rowHeight = ref(0)
let resizeObserver = null

const todayKey = computed(() => dayKey(props.now))
const weekdays = computed(() => daysBetween(props.rangeStart, props.rangeStart.add(DAYS_PER_WEEK, 'day'))
  .map(day => (props.compact ? formatters.value.weekdayNarrow : formatters.value.weekday).format(day.toDate())))

const isBar = (item) => isAllDayEvent(item.event) || item.from !== item.to
const visibleLanes = computed(() => Math.max(1, Math.floor((rowHeight.value - DATE_HEADER_HEIGHT - LANE_PADDING) / LANE_HEIGHT)))

const weeks = computed(() => daysBetween(props.rangeStart, props.rangeEnd)
  .filter((_, index) => index % DAYS_PER_WEEK === 0)
  .map(start => {
    const days = daysBetween(start, start.add(DAYS_PER_WEEK, 'day')).map(day => ({
      day,
      key: dayKey(day),
      today: dayKey(day) === todayKey.value,
      outside: !day.isSame(props.month, 'month'),
      rest: webSiteStore.isRestDay(day),
    }))
    const { items, laneCount } = packLanes(
      props.events.map(event => eventSpan(event, start, DAYS_PER_WEEK)).filter(Boolean),
    )
    const limit = laneCount > visibleLanes.value ? visibleLanes.value - 1 : laneCount
    return {
      key: dayKey(start),
      days,
      items: items.filter(item => item.lane < limit).map(item => ({ ...item, bar: isBar(item) })),
      hidden: days.map((_, index) => items.filter(item => item.lane >= limit && item.from <= index && item.to >= index).length),
      limit,
    }
  }))

const dayEvents = (day) => {
  const end = day.add(1, 'day')
  return props.events
    .filter(event => event.startAt.isBefore(end) && event.endAt.isAfter(day))
    .sort((a, b) => Number(isAllDayEvent(b)) - Number(isAllDayEvent(a)) || a.startAt.valueOf() - b.startAt.valueOf())
}

const itemStyle = (item) => ({
  left: `calc(${(item.from / DAYS_PER_WEEK) * 100}% + 0.1875rem)`,
  width: `calc(${((item.to - item.from + 1) / DAYS_PER_WEEK) * 100}% - 0.375rem)`,
  top: `${DATE_HEADER_HEIGHT + item.lane * LANE_HEIGHT}rem`,
  '--event-color': item.event.color,
})

const moreStyle = (index, limit) => ({
  left: `calc(${(index / DAYS_PER_WEEK) * 100}% + 0.1875rem)`,
  width: `calc(${100 / DAYS_PER_WEEK}% - 0.375rem)`,
  top: `${DATE_HEADER_HEIGHT + limit * LANE_HEIGHT}rem`,
})

const eventTitle = (event) => event.title || t('common.staff.absence')
const startTime = (event) => formatters.value.time.format(event.startAt.toDate())

const measure = () => {
  const root = rootRef.value
  if (!root) {
    return
  }
  const head = root.querySelector('.cal-month__head')
  rowHeight.value = pxToRem((root.clientHeight - (head?.offsetHeight ?? 0)) / Math.max(weeks.value.length, 1))
}

onMounted(() => {
  measure()
  resizeObserver = new ResizeObserver(measure)
  resizeObserver.observe(rootRef.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})
</script>
<template>
  <div
    ref="rootRef"
    class="cal-month"
    :class="{ 'cal-month--compact': compact }"
  >
    <div class="cal-month__head">
      <span
        v-for="(weekday, index) in weekdays"
        :key="`weekday-${index}`"
        class="cal-month__weekday"
      >{{ weekday }}</span>
    </div>
    <div class="cal-month__weeks" :style="{ '--week-count': weeks.length }">
      <div
        v-for="week in weeks"
        :key="week.key"
        class="cal-month__week"
      >
        <div
          v-for="cell in week.days"
          :key="cell.key"
          class="cal-month__cell"
          :data-day="cell.key"
          :class="{
            'cal-month__cell--outside': cell.outside,
            'cal-month__cell--rest': cell.rest,
            'cal-month__cell--today': cell.today,
          }"
          @click="emit('create', { start: cell.day, end: cell.day.add(1, 'day') })"
        >
          <button
            type="button"
            class="cal-month__date"
            @click.stop="emit('day-click', cell.day)"
          >
            <template v-if="cell.day.date() === 1">
              {{ formatters.monthShort.format(cell.day.toDate()) }}
            </template>
            {{ cell.day.date() }}
          </button>
        </div>
        <transition-group name="cal-event-fade">
          <div
            v-for="item in week.items"
            :key="`${item.event.id}-${week.key}`"
            :data-event-id="item.event.id"
            class="cal-event"
            :class="[
              item.bar ? ['cal-event--bar', eventClasses(item.event)] : 'cal-event--chip',
              item.bar && { 'cal-event--clip-start': item.clippedStart, 'cal-event--clip-end': item.clippedEnd },
            ]"
            :style="itemStyle(item)"
            @click.stop="emit('event-click', item.event)"
          >
            <span v-if="!item.bar" class="cal-event__dot" />
            <q-icon
              v-if="item.event.type === eventTypeEnum.ABSENCE"
              :name="ABSENCE_ICON"
              size="0.875rem"
            />
            <span v-if="!item.bar && !compact" class="cal-event__time">{{ startTime(item.event) }}</span>
            <span class="cal-event__title">{{ eventTitle(item.event) }}</span>
            <q-tooltip v-if="!compact" :delay="500">
              {{ eventTitle(item.event) }}<br>{{ formatTimeRange(formatters, item.event.startAt, item.event.endAt) }}
            </q-tooltip>
          </div>
        </transition-group>
        <template v-for="(hidden, index) in week.hidden" :key="`more-${week.key}-${index}`">
          <button
            v-if="hidden"
            type="button"
            class="cal-month__more"
            :style="moreStyle(index, week.limit)"
            @click.stop
          >
            {{ compact ? `+${hidden}` : t('common.moreCount', { count: hidden }) }}
            <q-menu
              anchor="top middle"
              self="top middle"
              class="cal-scope cal-month__popover"
              transition-show="scale"
              transition-hide="scale"
            >
              <div class="cal-month__popover-head">
                <span class="cal-month__popover-weekday">{{ formatters.weekday.format(week.days[index].day.toDate()) }}</span>
                <button
                  v-close-popup
                  type="button"
                  class="cal-month__popover-date"
                  @click="emit('day-click', week.days[index].day)"
                >
                  {{ week.days[index].day.date() }}
                </button>
              </div>
              <div class="cal-month__popover-list">
                <div
                  v-for="event in dayEvents(week.days[index].day)"
                  :key="event.id"
                  v-close-popup
                  class="cal-event cal-event--bar"
                  :class="eventClasses(event)"
                  :style="{ '--event-color': event.color }"
                  @click="emit('event-click', event)"
                >
                  <q-icon
                    v-if="event.type === eventTypeEnum.ABSENCE"
                    :name="ABSENCE_ICON"
                    size="0.875rem"
                  />
                  <span class="cal-event__title">{{ eventTitle(event) }}</span>
                </div>
              </div>
            </q-menu>
          </button>
        </template>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.cal-month {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.cal-month__head {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  flex-shrink: 0;
  border-bottom: 1px solid var(--cal-border);
}

.cal-month__weekday {
  padding: 0.5rem 0;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-align: center;
  text-transform: uppercase;
  color: var(--cal-text-muted);
}

.cal-month__weeks {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-rows: repeat(var(--week-count), minmax(0, 1fr));
}

.cal-month__week {
  position: relative;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  min-height: 0;
  overflow: hidden;
  border-bottom: 1px solid var(--cal-line);

  &:last-child {
    border-bottom: 0;
  }
}

.cal-month__cell {
  display: flex;
  justify-content: center;
  min-width: 0;
  padding-top: 0.25rem;
  border-left: 1px solid var(--cal-line);
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:first-child {
    border-left: 0;
  }

  &:hover {
    background: color-mix(in srgb, var(--q-primary) 4%, transparent);
  }
}

.cal-month__cell--rest {
  background: var(--cal-rest-day);
}

.cal-month__cell--today {
  background: var(--cal-today);
}

.cal-month__cell--outside .cal-month__date {
  color: var(--cal-text-muted);
  opacity: 0.6;
}

.cal-month__date {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 0.375rem;
  border: 0;
  border-radius: 1rem;
  background: transparent;
  color: var(--cal-text);
  font: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: color-mix(in srgb, var(--q-primary) 12%, transparent);
  }
}

.cal-month__cell--today .cal-month__date,
.cal-month__cell--today .cal-month__date:hover {
  background: var(--q-primary);
  color: white;
  font-weight: 700;
  box-shadow: 0 0.25rem 0.625rem -0.25rem var(--q-primary);
}

.cal-month .cal-event {
  position: absolute;
  height: 1.25rem;
}

.cal-month__more {
  position: absolute;
  height: 1.25rem;
  padding: 0 0.375rem;
  border: 0;
  border-radius: 0.375rem;
  background: transparent;
  color: var(--cal-text-muted);
  font: inherit;
  font-size: 0.6875rem;
  font-weight: 600;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: color-mix(in srgb, var(--q-primary) 8%, transparent);
    color: var(--q-primary);
  }
}

.cal-month--compact {
  .cal-month__date {
    min-width: 1.375rem;
    height: 1.375rem;
    padding: 0 0.25rem;
    font-size: 0.6875rem;
  }

  .cal-event {
    font-size: 0.625rem;
  }

  .cal-month__more {
    padding: 0 0.25rem;
    font-size: 0.625rem;
  }
}
</style>
<style lang="scss">
.cal-month__popover {
  width: 15rem;
  max-width: 92vw;
  padding: 0.75rem;
  border-radius: 1rem !important;
}

.cal-month__popover-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;
  margin-bottom: 0.5rem;
}

.cal-month__popover-weekday {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  opacity: 0.6;
}

.cal-month__popover-date {
  width: 2.25rem;
  height: 2.25rem;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 1.125rem;
  cursor: pointer;

  &:hover {
    background: color-mix(in srgb, var(--q-primary) 10%, transparent);
  }
}

.cal-month__popover-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: 50vh;
  overflow-y: auto;

  .cal-event {
    position: relative;
    height: 1.5rem;
  }
}
</style>
